import React from 'react';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Pressable, Text, View, StyleSheet, ScrollView } from 'react-native';
import { Paprika, PaprikaEntry } from '../../../../styles/theme';
import { useTranslation } from 'react-i18next';
import { formatDayMonth } from '../../../../utils/dateLocale';
import { BottomSheet } from '../primitives/BottomSheet';
import {
  leftoverRowMeta,
  FRIDGE_LEFTOVER_DAYS,
  type LeftoverCandidate,
} from '../leftovers';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { RemoteImage } from '../../../shared/RemoteImage';

interface Props {
  visible: boolean;
  fridge: LeftoverCandidate[];
  freezer: LeftoverCandidate[];
  dayLabel: string;
  /** Today as YYYY-MM-DD, injected so the expiry-warning rule stays testable. */
  today: string;
  resolve: (id: string) => RecipeIndexEntry | undefined;
  onPick: (c: LeftoverCandidate) => void;
  onClose: () => void;
}

export function LeftoverSheet({
  visible, fridge, freezer, dayLabel, today, resolve, onPick, onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const isEmpty = fridge.length === 0 && freezer.length === 0;
  const fmt = (iso: string) => formatDayMonth(iso, i18n.language, 'short');

  const renderRow = (c: LeftoverCandidate) => {
    const meta = leftoverRowMeta(c, t, (id) => resolve(id)?.title, fmt, today);
    const image = c.kind === 'fridge' ? resolve(c.refRecipeId)?.image : undefined;
    const toneStyle =
      meta.tone === 'warn' ? styles.pillWarn
      : meta.tone === 'muted' ? styles.pillMuted
      : styles.pillCount;
    const toneText =
      meta.tone === 'warn' ? styles.pillWarnText
      : meta.tone === 'muted' ? styles.pillMutedText
      : styles.pillCountText;

    return (
      <Pressable
        key={c.key}
        testID={`leftover-row-${c.key}`}
        style={[styles.row, meta.disabled && styles.rowDisabled]}
        disabled={meta.disabled}
        onPress={() => onPick(c)}
      >
        <View style={styles.photoWrap}>
          <RemoteImage
            uri={image}
            variant="thumb"
            style={styles.photo}
            resizeMode="cover"
            fallback={<View style={[styles.photo, styles.photoPlaceholder]}>
                <MaterialCommunityIcons
                  name={c.kind === 'freezer' ? 'snowflake' : 'recycle'}
                  size={18}
                  color={PaprikaEntry.leftover.color}
                />
              </View>}
          />
        </View>

        <View style={styles.rowInfo}>
          <Text style={styles.rowTitle} numberOfLines={1}>{meta.title}</Text>
          <Text style={styles.rowSub} numberOfLines={1}>{meta.sub}</Text>
        </View>

        {meta.badge !== undefined && (
          <View style={[styles.pill, toneStyle]}>
            <Text style={[styles.pillText, toneText]}>{meta.badge}</Text>
          </View>
        )}
      </Pressable>
    );
  };

  return (
    <BottomSheet visible={visible} onClose={onClose} maxHeight="80%">
      <View style={[styles.header, { backgroundColor: PaprikaEntry.leftover.tint }]}>
        <View style={styles.iconSquare}>
          <MaterialCommunityIcons name="recycle" size={20} color={PaprikaEntry.leftover.color} />
        </View>
        <View style={styles.headerText}>
          <Text style={styles.title}>{t('entry.leftovers')}</Text>
          <Text style={styles.subtitle}>
            {t('thisWeek.leftoverPickerSubtitle', { day: dayLabel })}
          </Text>
        </View>
        <Pressable style={styles.closeBtn} onPress={onClose} accessibilityLabel={t('common.close')}>
          <MaterialCommunityIcons name="close" size={18} color={Paprika.textSecondary} />
        </Pressable>
      </View>

      {isEmpty ? (
        <View style={styles.emptyState} testID="leftover-empty">
          <Text style={styles.emptyTitle}>{t('thisWeek.leftoverNoneTitle')}</Text>
          <Text style={styles.emptyText}>{t('thisWeek.leftoverNoneBody')}</Text>
        </View>
      ) : (
        <ScrollView style={styles.list} showsVerticalScrollIndicator={false}>
          {fridge.length > 0 && (
            <>
              <Text style={styles.sectionLabel}>
                {t('thisWeek.leftoverFridgeSection', { days: FRIDGE_LEFTOVER_DAYS })}
              </Text>
              {fridge.map(renderRow)}
            </>
          )}
          {freezer.length > 0 && (
            <>
              <Text style={[styles.sectionLabel, styles.sectionLabelFreezer]}>
                {t('thisWeek.leftoverFreezerSection')}
              </Text>
              {freezer.map(renderRow)}
            </>
          )}
        </ScrollView>
      )}
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 },
  iconSquare: { width: 44, height: 44, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  headerText: { flex: 1, gap: 4 },
  title: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 20, lineHeight: 26,
    color: Paprika.textPrimary,
  },
  subtitle: { fontFamily: 'Inter-Regular', fontSize: 13, color: Paprika.textSecondary },
  closeBtn: { width: 32, height: 32, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  list: { flex: 1 },
  sectionLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: PaprikaEntry.leftover.color,
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 4,
  },
  sectionLabelFreezer: { color: '#3a6a8a' },
  emptyState: {
    flex: 1, justifyContent: 'center', alignItems: 'center',
    paddingHorizontal: 24, paddingVertical: 36, gap: 6,
  },
  emptyTitle: {
    fontFamily: 'Inter-Medium', fontSize: 14,
    color: Paprika.textPrimary, textAlign: 'center',
  },
  emptyText: {
    fontFamily: 'Inter-Regular', fontSize: 13, lineHeight: 19,
    textAlign: 'center', color: Paprika.textSecondary,
  },
  row: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 16, paddingVertical: 10,
    gap: 12, marginVertical: 2,
  },
  rowDisabled: { opacity: 0.45 },
  photoWrap: { width: 48, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0 },
  photo: { width: 48, height: 48, justifyContent: 'center', alignItems: 'center' },
  photoPlaceholder: { backgroundColor: PaprikaEntry.leftover.tint },
  rowInfo: { flex: 1, gap: 2 },
  rowTitle: {
    fontFamily: 'Inter-Medium', fontSize: 13.5, lineHeight: 18,
    color: Paprika.textPrimary,
  },
  rowSub: { fontFamily: 'Inter-Regular', fontSize: 12, color: Paprika.textSecondary },
  pill: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 10, paddingVertical: 6, borderRadius: 12, flexShrink: 0,
  },
  pillCount: { backgroundColor: PaprikaEntry.leftover.tint },
  pillCountText: { color: PaprikaEntry.leftover.color },
  pillWarn: { backgroundColor: Paprika.accentLight },
  pillWarnText: { color: '#9a5730' },
  pillMuted: { backgroundColor: Paprika.surfaceHighest },
  pillMutedText: { color: Paprika.textMuted },
  pillText: { fontFamily: 'Inter-SemiBold', fontSize: 11 },
});
